import { EmptyStateComponent } from '@components/EmptyStateComponent'
import { HeroCarousel } from '@components/HeroCarousel'
import { Banner } from '@components/Home/Banner'
import { ContinueLessonsSection } from '@components/Home/Continue/ContinueLessonsSection'
import { CustomBanners } from '@components/Home/CustomBanners'
import { CustomProductList } from '@components/Home/CustomProductList'
import { HomeLoadingSkeleton } from '@components/Home/HomeLoadingSkeleton'
import { MyProductsSection } from '@components/Home/MyProducts/MyProductsSection'
import { BANNER_HEIGHT, BANNER_MAX_HEIGHT } from '@constants/homeBanners'
import { ContinueLessonsProvider } from '@contexts/ContinueLessonsContext'
import { IEnrollment } from '@dtos/Enrollment'
import { IContinueLessons } from '@dtos/User'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useMediaSize } from '@hooks/useMediaSize'
import { useMembersConfig } from '@hooks/useMembersConfig'
import { usePushValueMoment } from '@hooks/usePushValueMoment'
import { AppLayout } from '@layouts/AppLayout'
import { useGetContinueLessons } from '@services/api/graphql/continueLessons'
import { useGetUserEnrollments } from '@services/api/graphql/userEnrollments'
import {
  toPreviewEnrollment,
  useGetAdminPreviewProducts,
} from '@services/api/graphql/adminPreviewProducts'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useToastController } from '@tamagui/toast'
import { useFocusEffect } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTokens, useMedia, useWindowDimensions, View, YStack } from 'tamagui'

const PRODUCTS_PAGE_SIZE = 15

interface IMyProducts {
  totalProductsNumber: number
  products: IEnrollment[]
}

export function DefaultHomeScreen() {
  const { setCurrentLesson } = useCurrentLesson()
  const [continueLessons, setContinueLessons] = useState<
    IContinueLessons[] | null
  >(null)
  const [myProducts, setMyProducts] = useState<IMyProducts | null>(null)
  const [myProductsHasMore, setMyProductsHasMore] = useState(true)
  const toast = useToastController()
  const { height } = useWindowDimensions()
  const size = useMediaSize()
  const media = useMedia()
  const { t } = useTranslation('Home')
  const hasLoadedOnce = useRef(false)
  const {
    data: membersConfigData,
    loading: membersConfigLoading,
    called: membersConfigCalled,
  } = useMembersConfig()
  const {
    getContinueLessons,
    loading: continueLessonsLoading,
    called: continueLessonsCalled,
  } = useGetContinueLessons()
  const {
    getUserEnrollments,
    error: errorUserEnrollments,
    loading: loadingUserEnrollments,
    called: calledUserEnrollments,
  } = useGetUserEnrollments({
    offset: 0,
    limit: 15,
  })
  // Visão do produtor: não há matrícula nem progresso, então a lista de
  // produtos vem do escopo do painel e "continuar assistindo" fica vazio.
  const { isPreview } = useAdminPreview()
  usePushValueMoment(!isPreview && (myProducts?.totalProductsNumber ?? 0) > 0)
  const {
    getAdminPreviewProducts,
    loading: loadingPreviewProducts,
    called: calledPreviewProducts,
  } = useGetAdminPreviewProducts({ offset: 0, limit: PRODUCTS_PAGE_SIZE })

  const isSomethingLoading = useMemo(() => {
    if (isPreview) return membersConfigLoading || loadingPreviewProducts

    return (
      membersConfigLoading ||
      (loadingUserEnrollments && !calledUserEnrollments) ||
      continueLessonsLoading
    )
  }, [
    membersConfigLoading,
    loadingUserEnrollments,
    calledUserEnrollments,
    continueLessonsLoading,
    isPreview,
    loadingPreviewProducts,
  ])
  const bannerHeight = height * (BANNER_HEIGHT[size] || BANNER_HEIGHT.xs)
  const bannerMaxHeight = BANNER_MAX_HEIGHT[size] || BANNER_MAX_HEIGHT.xs
  const filteredActiveBanners = useMemo(
    () =>
      membersConfigData?.header?.banners.filter((banner) => banner.active) ||
      [],
    [membersConfigData],
  )
  const showBanners = filteredActiveBanners.length > 0
  const continueLessonsMarginTop = filteredActiveBanners.length > 0 ? 0 : 122
  const showProductsName = membersConfigData?.general?.showProductName ?? true
  const hasContinueLessons = continueLessons && continueLessons.length > 0
  const hasContentAboveTheSection =
    filteredActiveBanners.length > 0 || hasContinueLessons

  const myProductsMarginTop = hasContentAboveTheSection ? 0 : 122

  const sectionsGap = useMemo(() => {
    const sizes = getTokens().size
    if (media.gtXxl) return sizes['$spacing-xxl'].val
    if (media.gtLg) return sizes['$spacing-xsl'].val
    if (media.gtMd) return sizes['$spacing-xl'].val
    return sizes['$spacing-lg'].val
  }, [media.gtXxl, media.gtLg, media.gtMd])

  // Preview pagina pelo escopo do painel, não por matrícula: o colaborador
  // não tem nenhuma, e pedir getUserEnrollments aqui falhava e mostrava o
  // toast de erro no lugar do restante dos produtos.
  const handleLoadMorePreviewProducts = useCallback(
    async (offset: number) => {
      try {
        const { data, error } = await getAdminPreviewProducts({
          variables: { offset, limit: PRODUCTS_PAGE_SIZE },
        })
        if (error) {
          const isEmptyPage =
            error.graphQLErrors?.[0]?.extensions?.code === 'empty_page' ||
            error.message === 'Empty page!'
          if (isEmptyPage) {
            setMyProductsHasMore(false)
            return
          }
          throw error
        }
        const page = (data?.adminPreviewProducts ?? []).map(toPreviewEnrollment)
        const total = data?.adminPreviewProductsCount ?? 0
        setMyProductsHasMore(page.length > 0 && offset + page.length < total)
        setMyProducts((prev) => {
          if (!prev) return null
          return {
            products: [...prev.products, ...page],
            totalProductsNumber: total,
          }
        })
      } catch (error) {
        toast.show(t('myProducts.toast.error.title'), {
          message: t('myProducts.toast.error.message'),
          type: 'error',
        })
      }
    },
    [getAdminPreviewProducts, toast, t],
  )

  const handleLoadMoreProducts = useCallback(
    async (offset: number) => {
      if (isPreview) return handleLoadMorePreviewProducts(offset)
      try {
        const { data, error } = await getUserEnrollments({
          variables: {
            offset,
            limit: 15,
          },
        })
        if (error) {
          const isEmptyPage =
            error.graphQLErrors?.[0]?.extensions?.code === 'empty_page' ||
            error.message === 'Empty page!'
          if (isEmptyPage) {
            setMyProductsHasMore(false)
          }
          return
        }
        setMyProducts((prev) => {
          if (!prev) return null
          return {
            ...prev,
            products: [...prev.products, ...(data?.user.enrollments || [])],
            totalProductsNumber: prev.totalProductsNumber,
          }
        })
      } catch (error) {
        toast.show(t('myProducts.toast.error.title'), {
          message: t('myProducts.toast.error.message'),
          type: 'error',
        })
      }
    },
    [getUserEnrollments, toast, t, isPreview, handleLoadMorePreviewProducts],
  )

  const getPreviewHomeInfo = useCallback(async () => {
    try {
      // Offset explícito: o lazy query reaproveita as variáveis da última
      // chamada, e o refocus voltaria na página em que o load-more parou.
      const { data } = await getAdminPreviewProducts({
        variables: { offset: 0, limit: PRODUCTS_PAGE_SIZE },
      })
      const total = data?.adminPreviewProductsCount ?? 0
      const products = (data?.adminPreviewProducts ?? []).map(
        toPreviewEnrollment,
      )
      setContinueLessons([])
      setMyProducts({ totalProductsNumber: total, products })
      setMyProductsHasMore(products.length < total)
      hasLoadedOnce.current = true
    } catch (error) {
      toast.show(t('myProducts.toast.error.title'), {
        message: t('myProducts.toast.error.message'),
        type: 'error',
      })
    }
  }, [getAdminPreviewProducts, toast, t])

  const getHomeInfo = useCallback(async () => {
    try {
      const [{ data: userEnrollmentsData }, { data: continueLessonsData }] =
        await Promise.all([getUserEnrollments(), getContinueLessons()])

      if (userEnrollmentsData) {
        setMyProducts({
          totalProductsNumber: userEnrollmentsData.user.enrollmentsCount,
          products: userEnrollmentsData.user.enrollments,
        })
      }
      if (continueLessonsData) {
        setContinueLessons(continueLessonsData.user.lastViewedContents || [])
      } else {
        setContinueLessons([])
      }
      hasLoadedOnce.current = true
    } catch (error) {
      toast.show(t('myProducts.toast.error.title'), {
        message: t('myProducts.toast.error.message'),
        type: 'error',
      })
    }
  }, [getUserEnrollments, getContinueLessons, toast, t])

  useFocusEffect(
    useCallback(() => {
      if (isPreview) {
        getPreviewHomeInfo()
        return
      }
      getHomeInfo()
    }, [getHomeInfo, getPreviewHomeInfo, isPreview]),
  )

  const hasActiveBanners = useMemo(() => {
    return (
      membersConfigData &&
      !!membersConfigData?.header?.banners.some((banner) => banner.active)
    )
  }, [membersConfigData])
  const isSomethingLoadingAndNotCalled = useMemo(() => {
    const calledProducts = isPreview
      ? calledPreviewProducts
      : calledUserEnrollments

    return isSomethingLoading || !calledProducts || !membersConfigCalled
  }, [
    isSomethingLoading,
    calledUserEnrollments,
    membersConfigCalled,
    isPreview,
    calledPreviewProducts,
  ])
  const hasContent = useMemo(() => {
    return (
      (myProducts && myProducts.products.length !== 0 && membersConfigCalled) ||
      (hasActiveBanners &&
        membersConfigCalled &&
        (continueLessonsCalled || isPreview))
    )
  }, [
    myProducts,
    membersConfigCalled,
    hasActiveBanners,
    continueLessonsCalled,
    isPreview,
  ])
  const displayLoading = useMemo(() => {
    if (hasLoadedOnce.current) return false
    return (
      isSomethingLoadingAndNotCalled ||
      myProducts === null ||
      continueLessons === null
    )
  }, [isSomethingLoadingAndNotCalled, myProducts, continueLessons])

  useEffect(() => {
    setCurrentLesson(null)
  }, [setCurrentLesson])

  useEffect(() => {
    if (
      errorUserEnrollments &&
      errorUserEnrollments.message !== 'Empty page!'
    ) {
      toast.show(t('myProducts.toast.error.title'), {
        message: t('myProducts.toast.error.message'),
        type: 'error',
      })
    }
  }, [errorUserEnrollments, toast, t])

  if (displayLoading) return <HomeLoadingSkeleton />

  return (
    <AppLayout
      backRoute={{
        label: '',
        shouldDisplayLogo: true,
      }}
    >
      {hasContent ? (
        <View gap={sectionsGap} flexGrow={1}>
          {showBanners && (
            <View testID="hero-carousel">
              <HeroCarousel
                height={bannerHeight}
                maxHeight={bannerMaxHeight}
                numberOfSlides={filteredActiveBanners.length}
                displayControls={false}
                autoPlay={true}
                stopOnHover={false}
                autoPlayInterval={10000}
                indicatorsStyles={{
                  position: 'absolute',
                  width: '100%',
                  bottom: 28,
                  zIndex: 1000,
                  flex: 1,
                }}
              >
                {filteredActiveBanners.map((item, index) => {
                  return (
                    <Banner
                      key={index}
                      bannerData={item}
                      height={bannerHeight}
                    />
                  )
                })}
              </HeroCarousel>
            </View>
          )}
          {continueLessons && continueLessons.length > 0 && (
            <ContinueLessonsProvider
              continueLessons={continueLessons}
              isLoading={continueLessonsLoading}
            >
              <View marginTop={continueLessonsMarginTop}>
                <ContinueLessonsSection showProductName={showProductsName} />
              </View>
            </ContinueLessonsProvider>
          )}
          {myProducts && myProducts.products.length > 0 && (
            <YStack marginTop={myProductsMarginTop}>
              <MyProductsSection
                showProductName={showProductsName}
                data={myProducts.products}
                totalProductsNumber={myProducts.totalProductsNumber}
                hasMore={myProductsHasMore}
                handleLoadMoreProducts={() =>
                  handleLoadMoreProducts(myProducts.products.length)
                }
              />
            </YStack>
          )}
          <CustomProductList />
          <CustomBanners />
        </View>
      ) : (
        <View
          testID="empty-state-component"
          justifyContent="center"
          paddingHorizontal="$size.spacing-xs"
          $gtSm={{
            paddingHorizontal: '$size.spacing-xsl',
          }}
          flex={1}
          alignItems="center"
        >
          <EmptyStateComponent
            title={t('myProducts.emptyStateComponent.title')}
            description={t('myProducts.emptyStateComponent.description')}
            displayButton={false}
          />
        </View>
      )}
    </AppLayout>
  )
}
